import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {Handle, Position} from 'reactflow';

import {SequenceType} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getWidthForBaseCount, StaticBases} from 'modbio/polymer-maps/OligoMap';
import {ModifiedNucleotidesVisualization} from 'molbio/maps/linear/ModifiedNucleotides';
import Type from 'molbio/maps/Type';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';

import {
  ALL_HANDLES,
  HandleType,
  NucleotideStructureGraphNodeData,
  StructureGraphNodeData,
} from '../PolymerStructureGraphConstants';
import {useComplexPolymerDragAndDropCanvasContext} from './ComplexPolymerDragAndDropContext';
import {getActiveHandlesForNode, getxFn} from './ComplexPolymerDragAndDropUtil';

interface CustomNucleotideNodeProps {
  id: string;
  data: NucleotideStructureGraphNodeData;
}

function getTestPropsWithLegacyAttributesAndUniqueTestId(
  legacyTestProps: Parameters<typeof getTestProps>[0],
  uniqueTestProps: Parameters<typeof getTestProps>[0]
): ReturnType<typeof getTestProps> {
  const legacyProps = getTestProps(legacyTestProps);
  const uniqueProps = getTestProps(uniqueTestProps);
  return {...legacyProps, 'data-testid': uniqueProps['data-testid']};
}

export function CustomNucleotideNode({id, data}: CustomNucleotideNodeProps): JSX.Element {
  const type = new Type(20, 22);

  const basesWidth = getWidthForBaseCount(1);
  const xFn = getxFn(data.residueIndex, data.residueIndex + 1, 0, basesWidth, true);

  const {edges, isEditing} = useComplexPolymerDragAndDropCanvasContext();

  const activeHandles = isEditing ? ALL_HANDLES : getActiveHandlesForNode(id, edges);
  const handleStyle = {background: isEditing ? Color.BLUE_350 : Color.GRAY_500};

  return (
    <div
      {...getTestPropsWithLegacyAttributesAndUniqueTestId(
        {
          component: 'CustomNucleotideNode',
          element: data.polymerRef,
          key: data.residueIndex.toString(),
        },
        {component: 'CustomNucleotideNode', element: id}
      )}
    >
      {data.isForward && (
        <Handle
          {...getTestPropsWithLegacyAttributesAndUniqueTestId(
            {
              component: 'CustomNucleotideNode',
              element: 'handle',
              key: `${data.polymerRef}-${data.residueIndex}-${HandleType.TOP}`,
            },
            {
              component: 'CustomNucleotideNode',
              element: 'handle',
              key: `${id}-${HandleType.TOP}`,
            }
          )}
          className={css(styles.handle)}
          style={{...handleStyle, visibility: activeHandles.includes(HandleType.TOP) ? 'visible' : 'hidden'}}
          id={HandleType.TOP}
          type="source"
          position={Position.Top}
          isConnectable={isEditing}
        />
      )}
      <div className={css(styles.nucleotideNode)}>
        <svg className={css(styles.nucleotideSvg)}>
          <g transform="translate(3,0)">
            <BenchlingQuery<MonomerQuery>
              query={MONOMER_QUERY}
              renderLoading={() => <div className={css(styles.loadingNode)} />}
            >
              {(monomerData) => (
                <ModifiedNucleotidesVisualization
                  bases={data.base}
                  sequenceMods={data.modifiedNucleotide ? [data.modifiedNucleotide] : []}
                  monomers={monomerData.monomers}
                  isOligo={true}
                  nucleotideType={data.nucleotideType}
                  xFn={xFn}
                  sliceStart={data.residueIndex}
                  sliceEnd={data.residueIndex + 1}
                  layout={{vMargin: -8.5}}
                  boxHeightToDNA={0}
                  showNucleotideStructures={true}
                  keyPrefix="complexPolymerMap"
                  displayBasesAsNatural={false}
                  includeBackground={true}
                  isForwardStrand={data.isForward}
                  showOverflow={true}
                />
              )}
            </BenchlingQuery>
            <StaticBases
              bases={data.base}
              // StaticBases expects the modified nucleotides to be passed in relative to the bases, not to the entire oligo.
              // Since we only ever pass in a single base and a modified nucleotide that is expected to correspond to it,
              // we can just pass in the position of 0 for the modified nucleotide
              modifiedNucleotides={data.modifiedNucleotide ? [{...data.modifiedNucleotide, position: 0}] : []}
              nucleotideType={data.nucleotideType}
              sequenceType={SequenceType.OLIGO}
              fontSize={type.fontSize}
              letterSpacing={type.letterSpacing}
              vMargin={0}
              isTemplate={false}
            />
          </g>
        </svg>
      </div>
      {!data.isForward && (
        <Handle
          {...getTestPropsWithLegacyAttributesAndUniqueTestId(
            {
              component: 'CustomNucleotideNode',
              element: 'handle',
              key: `${data.polymerRef}-${data.residueIndex}-${HandleType.BOTTOM}`,
            },
            {
              component: 'CustomNucleotideNode',
              element: 'handle',
              key: `${id}-${HandleType.BOTTOM}`,
            }
          )}
          className={css(styles.handle)}
          style={{
            ...handleStyle,
            visibility: activeHandles.includes(HandleType.BOTTOM) ? 'visible' : 'hidden',
          }}
          id={HandleType.BOTTOM}
          type="target"
          position={Position.Bottom}
          isConnectable={isEditing}
        />
      )}
    </div>
  );
}

interface CustomConjugateChipNodeProps {
  id: string;
  data: StructureGraphNodeData;
  dragging: boolean;
  selected: boolean;
}

export function CustomConjugateChipNode({
  id,
  data,
  dragging,
  selected,
}: CustomConjugateChipNodeProps): JSX.Element {
  const {edges, isEditing} = useComplexPolymerDragAndDropCanvasContext();

  const activeHandles = isEditing ? ALL_HANDLES : getActiveHandlesForNode(id, edges);
  const handleStyle = {background: isEditing ? Color.BLUE_350 : Color.GRAY_500};

  return (
    <div
      {...getTestProps({component: 'CustomConjugateChipNode', element: data.polymerRef})}
      className={css(styles.conjugateChipWrapper)}
      style={{pointerEvents: dragging || selected ? 'none' : 'all'}}
    >
      <Handle
        {...getTestProps({
          component: 'CustomConjugateChipNode',
          element: 'handle',
          key: `${id}-${HandleType.TOP}`,
        })}
        className={css(styles.handle)}
        style={{...handleStyle, visibility: activeHandles.includes(HandleType.TOP) ? 'visible' : 'hidden'}}
        id={HandleType.TOP}
        type="source"
        position={Position.Top}
        isConnectable={isEditing}
      />
      <Handle
        {...getTestProps({
          component: 'CustomConjugateChipNode',
          element: 'handle',
          key: `${id}-${HandleType.RIGHT}`,
        })}
        className={css(styles.handle)}
        style={{...handleStyle, visibility: activeHandles.includes(HandleType.RIGHT) ? 'visible' : 'hidden'}}
        id={HandleType.RIGHT}
        type="source"
        position={Position.Right}
        isConnectable={isEditing}
      />

      <Handle
        {...getTestProps({
          component: 'CustomConjugateChipNode',
          element: 'handle',
          key: `${id}-${HandleType.BOTTOM}`,
        })}
        className={css(styles.handle)}
        style={{...handleStyle, visibility: activeHandles.includes(HandleType.BOTTOM) ? 'visible' : 'hidden'}}
        id={HandleType.BOTTOM}
        type="target"
        position={Position.Bottom}
        isConnectable={isEditing}
      />
      <Handle
        {...getTestProps({
          component: 'CustomConjugateChipNode',
          element: 'handle',
          key: `${id}-${HandleType.LEFT}`,
        })}
        className={css(styles.handle)}
        style={{...handleStyle, visibility: activeHandles.includes(HandleType.LEFT) ? 'visible' : 'hidden'}}
        id={HandleType.LEFT}
        type="target"
        position={Position.Left}
        isConnectable={isEditing}
      />
      <BioentityChip className={css(styles.conjugateChip)} id={data.entityId} size={ChipSize.SMALL} />
    </div>
  );
}

const styles = StyleSheet.create({
  nucleotideNode: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    shapeRendering: 'crispEdges',
    height: '31px',
    width: '37px',
    pointerEvents: 'all',
  },
  nucleotideSvg: {
    height: 40,
    width: 40,
  },
  conjugateChip: {
    maxWidth: 125,
    border: `2px solid ${Color.WHITE}`,
    userSelect: 'none',
  },
  conjugateChipWrapper: {
    marginRight: '-8px',
  },
  handle: {
    height: 6,
    width: 6,
  },
  loadingNode: {
    height: 30,
    width: 30,
    borderRadius: '100%',
    background: Color.GRAY_250,
    opacity: 0.4,
  },
});
